'use client';

/**
 * AnswerForm — the contributor's answer-by-source form (WS-K Contribute centerpiece).
 *
 * A logged-in contributor answers an open question by POINTING TO A SOURCE: a source URL
 * (required), a sourceType, an optional note. On submit it POSTs to the auth-gated write
 * API (/api/open-questions/[id]/answers) via authenticatedFetch — or, in local fixtures
 * mode, with the dev-shim header so the loop is demonstrable without real Twitter auth.
 *
 * The answer is a SIGNAL, not an assertion: the server derives the asserter from the authed
 * uid and forces status='proposed'. On success we show the appended answer attributed to the
 * current user, linked to their /contributors/<handle> profile.
 *
 * Form is shown ONLY to logged-in users (useAuth().userData); the parent gates logged-out
 * users via ContributeGuard's sign-in affordance. Tokens-only styling (CARD vocab, mono
 * labels, scout-gold accent) — no new colors, no shadcn.
 */

import React from 'react';
import Link from 'next/link';
import { useAuth } from '@/contexts/AuthContext';
import { authenticatedFetch } from '@/lib/auth-utils';
import type { OpenQuestion, OpenQuestionAnswer } from '@/types/open-questions';

/** sourceType options offered to a contributor (matches the API enum). */
const SOURCE_TYPES: Array<{ value: OpenQuestionAnswer['sourceType']; label: string }> = [
  { value: 'official-bio', label: 'Official source (site / bio / docs)' },
  { value: 'onchain', label: 'On-chain (explorer / contract)' },
  { value: 'news', label: 'News / independent reporting' },
  { value: 'human-confirmed', label: 'Direct confirmation' },
];

/** Handle = the part after the asserter namespace prefix (links to the profile). */
function handleFromAsserter(asserter: string): string {
  return asserter
    .replace(/^human:contributor:/, '')
    .replace(/^human:member:/, '')
    .replace(/^human:/, '')
    .replace(/^model:/, '');
}

function StatusPill({ status }: { status: string }) {
  const cls =
    status === 'confirmed'
      ? 'border-signal-green-text/40 text-signal-green-text'
      : 'border-scout-gold/30 text-scout-gold';
  return (
    <span
      className={`inline-flex items-center rounded-[3px] border-[0.5px] px-1.5 py-px font-mono text-[8.5px] font-bold uppercase tracking-[0.1em] ${cls}`}
    >
      {status}
    </span>
  );
}

export default function AnswerForm({
  question,
  fixturesMode,
  entityOwnerUid,
}: {
  question: OpenQuestion;
  /** True when the app runs under USE_LOCAL_FIXTURES — enables the dev-shim header path. */
  fixturesMode: boolean;
  /**
   * The verified owner's uid for THIS question's entity (read server-side from tags.ownerUid),
   * or null/undefined when unclaimed / unknown. DISPLAY-ONLY: it gates a calm framing line for the
   * one user who owns the entity. It NEVER changes what is submitted — the server still forces every
   * answer to status='proposed' (the COI line). Identity buys the right to POINT, never to ASSERT.
   */
  entityOwnerUid?: string | null;
}) {
  const { userData } = useAuth();

  // Right-of-reply framing signal: is the current responder the VERIFIED owner of this entity?
  // Matches the SAME tags.ownerUid the server reads to derive the `human:member:<uid>` asserter.
  const isOwner = !!entityOwnerUid && !!userData?.id && entityOwnerUid === userData.id;

  const [url, setUrl] = React.useState('');
  const [sourceType, setSourceType] =
    React.useState<OpenQuestionAnswer['sourceType']>('official-bio');
  const [note, setNote] = React.useState('');
  const [pending, setPending] = React.useState(false);
  const [error, setError] = React.useState('');
  const [added, setAdded] = React.useState<OpenQuestionAnswer | null>(null);

  // Defense-in-depth: the form only renders for logged-in users (ContributeGuard already
  // gates logged-out users), but never show the form if userData is absent in fixtures mode.
  const loggedIn = !!userData || fixturesMode;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!url.trim()) {
      setError('A source URL is required.');
      return;
    }
    setPending(true);
    try {
      const endpoint = `/api/open-questions/${encodeURIComponent(question.id)}/answers`;
      const body = JSON.stringify({ url: url.trim(), sourceType, note: note.trim() });

      // In fixtures/dev mode (no real Twitter auth), use a plain fetch with the dev-shim
      // header so the loop is demonstrable locally. Otherwise the authed Firebase path.
      const res = fixturesMode
        ? await fetch(endpoint, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'x-dev-uid': 'dev-contributor' },
            body,
          })
        : await authenticatedFetch(endpoint, { method: 'POST', body });

      const json = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(json?.error || `Request failed (${res.status})`);
      }
      // The API returns the updated question; the appended answer is the last one.
      const updated = json.question as OpenQuestion | undefined;
      const appended =
        updated?.answers?.[updated.answers.length - 1] ??
        ({
          sourceType,
          url: url.trim(),
          snippet: note.trim() || undefined,
          observedAt: new Date().toISOString(),
          asserter: json.asserter || 'human:contributor:you',
          verificationMethod: 'news',
          status: 'proposed',
        } as OpenQuestionAnswer);
      setAdded(appended);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Submission failed. Please try again.');
    } finally {
      setPending(false);
    }
  };

  // ── Success state — the appended, attributed answer ───────────────────────────────
  if (added) {
    const handle = handleFromAsserter(added.asserter);
    return (
      <div className="rounded border-[0.5px] border-scout-gold/40 bg-scout-gold-dim/40 p-5">
        <p className="mb-2 font-mono text-[10px] font-semibold uppercase tracking-[0.14em] text-scout-gold">
          Answer added — thank you
        </p>
        <p className="mb-4 font-body text-[13.5px] leading-relaxed text-parchment-dim">
          Your source is on the record as a <span className="text-parchment">proposed</span>{' '}
          signal. It earns <span className="text-parchment">confirmed</span> when an
          independent source corroborates it — and you&apos;re{' '}
          <span className="text-parchment">credited on the record</span>, GitHub-style.
          Recognition, not money.
        </p>
        <div className="rounded border-[0.5px] border-divider bg-ink/60 px-3 py-2.5">
          <div className="mb-1.5 flex flex-wrap items-center gap-2">
            <StatusPill status={added.status} />
            <Link
              href={`/contributors/${encodeURIComponent(added.asserter)}`}
              className="font-mono text-[10px] tracking-[0.02em] text-slate-light transition-colors hover:text-scout-gold"
            >
              {handle} ↗
            </Link>
            {added.url && (
              <>
                <span className="text-divider">·</span>
                <a
                  href={added.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-mono text-[10px] tracking-[0.02em] text-slate-light transition-colors hover:text-scout-gold"
                >
                  source ↗
                </a>
              </>
            )}
          </div>
          {added.snippet && (
            <p className="text-[12.5px] leading-snug text-parchment-dim">“{added.snippet}”</p>
          )}
        </div>
        <div className="mt-4 flex flex-wrap gap-3">
          <Link
            href={`/contributors/${encodeURIComponent(added.asserter)}`}
            className="inline-flex items-center gap-1.5 rounded border-[0.5px] border-scout-gold/40 bg-scout-gold-dim px-3 py-1.5 font-mono text-[10px] font-semibold uppercase tracking-[0.08em] text-scout-gold transition-colors hover:bg-scout-gold/20"
          >
            View your contributions →
          </Link>
          <Link
            href="/open-questions"
            className="inline-flex items-center gap-1.5 rounded border-[0.5px] border-divider px-3 py-1.5 font-mono text-[10px] font-semibold uppercase tracking-[0.08em] text-parchment-dim transition-colors hover:border-scout-gold/30 hover:text-scout-gold"
          >
            Browse the board
          </Link>
        </div>
      </div>
    );
  }

  // ── The form ──────────────────────────────────────────────────────────────────────
  if (!loggedIn) {
    return (
      <p className="rounded border-[0.5px] border-divider px-4 py-3 font-body text-[13px] text-parchment-dim">
        Sign in to answer this question.
      </p>
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="rounded border-[0.5px] border-divider bg-surface p-5"
    >
      <p className="mb-4 font-mono text-[10px] font-semibold uppercase tracking-[0.14em] text-scout-gold">
        Answer with a source
      </p>

      {/* Right-of-reply framing — shown ONLY to the verified owner of this entity. Calm, not a
          megaphone: owning the entity buys the right to POINT, never to ASSERT. The line states
          the COI boundary out loud — still proposed, still corroborated, never the verdict. */}
      {isOwner && (
        <p className="mb-4 rounded border-[0.5px] border-divider bg-ink/50 px-3 py-2 font-mono text-[10px] leading-relaxed tracking-[0.02em] text-parchment-dim">
          You&apos;re the verified owner of{' '}
          <span className="text-parchment">{question.entitySlug || 'this entity'}</span> — your
          response is still a proposed signal, still corroborated, never the verdict.
        </p>
      )}

      <label className="mb-1.5 block font-mono text-[10px] uppercase tracking-[0.1em] text-parchment-dim">
        Source URL <span className="text-scout-gold">*</span>
      </label>
      <input
        type="url"
        required
        value={url}
        onChange={(e) => setUrl(e.target.value)}
        placeholder="https://…"
        className="mb-4 w-full rounded border-[0.5px] border-divider bg-ink px-3 py-2 font-mono text-[13px] text-parchment outline-none transition-colors placeholder:text-parchment-dim/50 focus:border-scout-gold/50"
      />

      <label className="mb-1.5 block font-mono text-[10px] uppercase tracking-[0.1em] text-parchment-dim">
        Source type
      </label>
      <select
        value={sourceType}
        onChange={(e) => setSourceType(e.target.value as OpenQuestionAnswer['sourceType'])}
        className="mb-4 w-full rounded border-[0.5px] border-divider bg-ink px-3 py-2 font-mono text-[13px] text-parchment outline-none transition-colors focus:border-scout-gold/50"
      >
        {SOURCE_TYPES.map((s) => (
          <option key={s.value} value={s.value}>
            {s.label}
          </option>
        ))}
      </select>

      <label className="mb-1.5 block font-mono text-[10px] uppercase tracking-[0.1em] text-parchment-dim">
        Note <span className="normal-case tracking-normal text-parchment-dim/60">(optional — quote the relevant line)</span>
      </label>
      <textarea
        value={note}
        onChange={(e) => setNote(e.target.value)}
        rows={3}
        maxLength={1000}
        placeholder="e.g. the dapp page shows 4,200 active users in the last 30 days…"
        className="mb-4 w-full resize-y rounded border-[0.5px] border-divider bg-ink px-3 py-2 font-body text-[13px] leading-relaxed text-parchment outline-none transition-colors placeholder:text-parchment-dim/50 focus:border-scout-gold/50"
      />

      {error && <p className="mb-3 font-body text-[12.5px] text-red-400">{error}</p>}

      <div className="flex flex-wrap items-center gap-3">
        <button
          type="submit"
          disabled={pending}
          className="inline-flex items-center gap-2 rounded border-[0.5px] border-scout-gold/40 bg-scout-gold/10 px-4 py-2 font-mono text-[11px] font-semibold uppercase tracking-[0.1em] text-scout-gold transition hover:bg-scout-gold/20 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {pending ? 'Submitting…' : 'Submit answer'}
        </button>
        <span className="font-mono text-[9.5px] uppercase tracking-[0.08em] text-parchment-dim/70">
          Lands as a proposed signal, attributed to you
        </span>
      </div>
    </form>
  );
}
